import { useState } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useCatalogSearch } from '../hooks/useExternalCatalog'
import type { SearchParams } from '../api/externalCatalogApi'

const ORDERS = [
  { value: 'recommended', label: 'Recommended' },
  { value: 'best_selling', label: 'Best selling' },
  { value: 'trending', label: 'Trending' },
  { value: 'recently_added', label: 'Newest' },
]

const megapixels = (w: number | null, h: number | null) =>
  w && h ? `${Math.round((w * h) / 1_000_000)} MP` : null

/**
 * Live search of the vendor's catalog. Read-only: results are not stored, and nothing here can
 * queue a download — ingest happens from an artist's page, once we know whose work it is.
 */
export function CatalogSearch({
  provider,
  onOpenArtist,
}: {
  provider: string
  onOpenArtist: (externalArtistId: string) => void
}) {
  const [draft, setDraft] = useState('')
  const [params, setParams] = useState<SearchParams | null>(null)
  const { data, isFetching, error } = useCatalogSearch(provider, params ?? {}, params !== null)

  const run = (next: Partial<SearchParams>) =>
    setParams((current) => ({ order: 'recommended', perPage: 30, ...current, page: 1, ...next }))

  return (
    <div className="space-y-4">
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          run({ q: draft.trim() || undefined })
        }}
      >
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Search titles and keywords…"
          className="max-w-sm"
        />
        <Button type="submit">Search</Button>
        <div className="flex flex-wrap gap-1">
          {ORDERS.map((o) => (
            <Button
              key={o.value}
              type="button"
              size="sm"
              variant={(params?.order ?? 'recommended') === o.value ? 'default' : 'ghost'}
              onClick={() => run({ q: draft.trim() || undefined, order: o.value })}
            >
              {o.label}
            </Button>
          ))}
        </div>
      </form>

      {params === null && (
        <p className="py-10 text-center text-sm text-slate-500">
          Search the catalog, or pick an ordering to browse it.
        </p>
      )}

      {error && (
        <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          The catalog did not answer this search. Try again; if it keeps failing, check the
          provider's configuration.
        </p>
      )}

      {isFetching && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
          {Array.from({ length: 12 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square w-full" />
          ))}
        </div>
      )}

      {!isFetching && data && data.artworks.length === 0 && (
        <p className="py-10 text-center text-sm text-slate-500">Nothing matched.</p>
      )}

      {!isFetching && data && data.artworks.length > 0 && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            {data.artworks.map((a) => (
              <div key={a.externalId} className="space-y-1">
                <a href={a.sourceUrl} target="_blank" rel="noreferrer" className="block">
                  {a.thumbUrl ? (
                    <img
                      src={a.thumbUrl}
                      alt={a.title ?? ''}
                      loading="lazy"
                      className="aspect-square w-full rounded-md bg-slate-100 object-cover"
                    />
                  ) : (
                    <div className="aspect-square w-full rounded-md bg-slate-100" />
                  )}
                </a>
                <div className="truncate text-xs font-medium" title={a.title ?? undefined}>
                  {a.title || 'Untitled'}
                </div>
                {a.externalArtistId ? (
                  <button
                    type="button"
                    className="block max-w-full truncate text-left text-xs text-blue-700 hover:underline"
                    onClick={() => onOpenArtist(a.externalArtistId!)}
                  >
                    {a.artistName ?? `Artist ${a.externalArtistId}`}
                  </button>
                ) : (
                  <div className="truncate text-xs text-slate-500">{a.artistName}</div>
                )}
                <div className="text-[11px] text-slate-400">
                  {[megapixels(a.width, a.height), a.grade ? `Grade ${a.grade}` : null]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between pt-2">
            <span className="text-xs text-slate-500">Page {data.page}</span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={data.page <= 1}
                onClick={() => setParams((p) => ({ ...p, page: data.page - 1 }))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!data.hasMore}
                onClick={() => setParams((p) => ({ ...p, page: data.page + 1 }))}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
